import { FormEvent, useEffect, useRef, useState } from 'react'
import { AlertCircle, ArrowRight, CalendarCheck, Check, ChevronDown, Loader2, ShieldCheck, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { captureAttribution, getGtagAttributionParams } from '@/lib/attribution'
import { submitBackendLead } from '@/lib/submitToBackend'
import { getLocaleFromPathname, href, toLogicalPath } from '@/i18n/locale'
import { cn } from '@/lib/utils'
import { fwConsentClass, fwErrorClass, fwInputClass, fwInputErrorClass, fwSubmitClass } from '@/lib/formStyles'
import { COUNTRIES, DEFAULT_COUNTRY, isValidPhone, normalizePhone, type Country } from '@/lib/phone'
import { FlagImg } from './GuideLeadPopup'

const POPUP_PATH = '/resources/whatsapp-bloque-24-heures-api-officielle'
const DELAY_MS = 10_000
const DISMISSED_KEY = 'fw_wa_suspension_popup_dismissed'
const CLAIMED_KEY = 'fw_wa_suspension_popup_claimed'

type AnalyticsWindow = Window & {
  gtag?: (...args: unknown[]) => void
  fbq?: (...args: unknown[]) => void
  __activeMetaPixelId?: string
}

type Field = 'name' | 'phone'

const copy = {
  fr: {
    eyebrow: 'Compte WhatsApp suspendu ?',
    title: 'On vous aide à débloquer votre WhatsApp Business',
    body: 'Réservez une consultation gratuite : on analyse la suspension, on vous accompagne pour récupérer le compte et on met en place la bonne configuration.',
    benefits: [
      'Diagnostic de la suspension par un expert',
      'Accompagnement pour récupérer votre compte',
      'Garantie à vie : ça ne se reproduira pas',
    ],
    name: 'Nom complet',
    namePlaceholder: 'Votre nom complet',
    phone: 'Numéro de téléphone',
    phonePlaceholder: '06 00 00 00 00',
    country: 'Choisir le pays',
    isWhatsApp: 'Ce numéro est sur WhatsApp',
    errors: { name: 'Indiquez votre nom complet.', phone: 'Indiquez un numéro de téléphone valide.' },
    formError: "Nous n'avons pas pu enregistrer votre demande. Réessayez dans un instant.",
    privacy: 'Vos coordonnées restent confidentielles et ne sont jamais revendues.',
    privacyLink: 'Confidentialité',
    submit: 'Réserver ma consultation gratuite',
    sending: 'Envoi en cours…',
    close: 'Fermer',
    doneEyebrow: 'Demande reçue',
    doneTitle: 'On vous rappelle très vite',
    doneBody: (first: string) => `Merci ${first}. Un expert vous contacte au numéro indiqué pour planifier votre consultation gratuite.`,
    doneButton: 'Continuer la lecture',
  },
  en: {
    eyebrow: 'WhatsApp account suspended?',
    title: 'Get help fixing your WhatsApp Business suspension',
    body: 'Book a free consultation: we review the suspension, help you recover the account and set up the right configuration.',
    benefits: [
      'Expert diagnosis of the suspension',
      'Hands-on help recovering your account',
      'Lifetime guarantee: it won’t happen again',
    ],
    name: 'Full name',
    namePlaceholder: 'Your full name',
    phone: 'Phone number',
    phonePlaceholder: '06 00 00 00 00',
    country: 'Choose country',
    isWhatsApp: 'This number is on WhatsApp',
    errors: { name: 'Enter your full name.', phone: 'Enter a valid phone number.' },
    formError: "We couldn't save your request. Please try again in a moment.",
    privacy: 'Your details stay confidential and are never resold.',
    privacyLink: 'Privacy',
    submit: 'Book my free consultation',
    sending: 'Sending…',
    close: 'Close',
    doneEyebrow: 'Request received',
    doneTitle: 'We’ll call you back shortly',
    doneBody: (first: string) => `Thanks ${first}. An expert will contact you on that number to schedule your free consultation.`,
    doneButton: 'Keep reading',
  },
  ar: {
    eyebrow: 'تم تعليق حساب واتساب؟',
    title: 'نساعدك على حل مشكلة تعليق واتساب بزنس',
    body: 'احجز استشارة مجانية: نحلل سبب التعليق، نرافقك لاسترجاع الحساب ونضع الإعداد الصحيح.',
    benefits: [
      'تشخيص سبب التعليق من طرف خبير',
      'مرافقة لاسترجاع حسابك',
      'ضمان مدى الحياة: لن يتكرر ذلك',
    ],
    name: 'الاسم الكامل',
    namePlaceholder: 'اسمك الكامل',
    phone: 'رقم الهاتف',
    phonePlaceholder: '06 00 00 00 00',
    country: 'اختر البلد',
    isWhatsApp: 'هذا الرقم على واتساب',
    errors: { name: 'أدخل اسمك الكامل.', phone: 'أدخل رقم هاتف صحيح.' },
    formError: 'تعذر تسجيل طلبك. حاول مرة أخرى بعد لحظة.',
    privacy: 'بياناتك تبقى سرية ولا تُباع أبدًا.',
    privacyLink: 'الخصوصية',
    submit: 'احجز استشارتي المجانية',
    sending: 'جارٍ الإرسال…',
    close: 'إغلاق',
    doneEyebrow: 'تم استلام طلبك',
    doneTitle: 'سنتصل بك قريبًا',
    doneBody: (first: string) => `شكرًا ${first}. سيتصل بك خبير على الرقم المُدخل لتحديد موعد استشارتك المجانية.`,
    doneButton: 'متابعة القراءة',
  },
} as const

export function WhatsAppSuspensionPopup() {
  const { pathname, search } = useLocation()
  const eligible = toLogicalPath(pathname) === POPUP_PATH
  const forceOpen = new URLSearchParams(search).get('lead') === 'open'
  const locale = getLocaleFromPathname(pathname)
  const text = copy[locale]
  const [open, setOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [showPicker, setShowPicker] = useState(false)
  const [phoneFocused, setPhoneFocused] = useState(false)
  const [isWhatsApp, setIsWhatsApp] = useState(true)
  const [touched, setTouched] = useState<Record<Field, boolean>>({ name: false, phone: false })
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
  const [formError, setFormError] = useState('')
  const dialog = useRef<HTMLDivElement>(null)
  const countryRef = useRef<HTMLDivElement>(null)

  const dismiss = () => {
    if (!forceOpen) {
      try { sessionStorage.setItem(DISMISSED_KEY, '1') } catch { /* Storage may be disabled. */ }
    }
    setOpen(false)
  }

  useEffect(() => {
    setOpen(false)
    setSubmitted(false)
    setShowPicker(false)
    if (!eligible) return

    const show = () => {
      captureAttribution()
      setOpen(true)
    }

    if (forceOpen) {
      show()
      return
    }

    try {
      if (sessionStorage.getItem(DISMISSED_KEY) || localStorage.getItem(CLAIMED_KEY)) return
    } catch { /* Show the popup when storage is unavailable. */ }

    const timer = window.setTimeout(show, DELAY_MS)
    return () => clearTimeout(timer)
  }, [eligible, forceOpen, pathname, search])

  useEffect(() => {
    if (!open) return
    const oldOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusTimer = window.setTimeout(() => dialog.current?.focus(), 0)
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') dismiss() }
    document.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(focusTimer)
      document.body.style.overflow = oldOverflow
      document.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!showPicker) return
    const onPointer = (event: MouseEvent) => {
      if (!countryRef.current?.contains(event.target as Node)) setShowPicker(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [showPicker])

  function validate(field: Field, value: string, dial = country.dial) {
    const ok = field === 'name' ? value.trim().length >= 2 : isValidPhone(value, dial)
    setErrors((current) => {
      const next = { ...current }
      if (ok) delete next[field]
      else next[field] = text.errors[field]
      return next
    })
    return ok
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormError('')
    setTouched({ name: true, phone: true })
    const nameOk = validate('name', name)
    const phoneOk = validate('phone', phone)
    if (!nameOk || !phoneOk) return
    setSubmitting(true)
    try {
      await submitBackendLead({
        landingSource: `wa-suspension-popup:${locale}`,
        name: name.trim(),
        phone: normalizePhone(phone, country.dial),
        notes: `[CONSULTATION GRATUITE - SUSPENSION WHATSAPP BUSINESS]\nAide pour débloquer un compte WhatsApp Business suspendu.\nPage: ${pathname}\nWhatsApp: ${isWhatsApp ? 'OUI' : 'NON'}\nPays: ${country.name} (${country.dial})`,
      })
      try {
        localStorage.setItem(CLAIMED_KEY, String(Date.now()))
        sessionStorage.setItem(DISMISSED_KEY, '1')
      } catch { /* Conversion is already saved. */ }
      const analytics = window as AnalyticsWindow
      analytics.gtag?.('event', 'lead_submit', { event_category: 'Consultation', event_label: 'WhatsApp Suspension Help', ...getGtagAttributionParams() })
      if (analytics.fbq && analytics.__activeMetaPixelId) {
        analytics.fbq('trackSingle', analytics.__activeMetaPixelId, 'Lead', { content_name: 'WhatsApp Suspension Help' })
      }
      setSubmitted(true)
    } catch {
      setFormError(text.formError)
    } finally {
      setSubmitting(false)
    }
  }

  if (!eligible || !open) return null

  const nameInvalid = Boolean(errors.name && touched.name)
  const phoneInvalid = Boolean(errors.phone && touched.phone)

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-fw-ink/70 font-fw overscroll-none sm:items-center sm:p-6"
      onMouseDown={(event) => event.target === event.currentTarget && dismiss()}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wa-suspension-popup-title"
        tabIndex={-1}
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
        className="relative flex max-h-[92dvh] w-full max-w-[520px] flex-col overflow-y-auto rounded-t-[24px] border border-fw-hairline bg-fw-white shadow-fw-panel outline-none sm:rounded-[24px]"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label={text.close}
          className="absolute end-3 top-3 z-20 grid h-11 w-11 place-items-center rounded-full border border-fw-hairline bg-fw-pure text-fw-graphite transition hover:border-fw-violet-border hover:text-fw-ink sm:end-4 sm:top-4"
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        {submitted ? (
          <div className="flex flex-col items-center px-5 py-14 text-center sm:px-10">
            <span className="mb-6 grid h-16 w-16 place-items-center rounded-full border border-fw-lavender-border bg-fw-lavender text-fw-violet">
              <CalendarCheck size={24} strokeWidth={2} aria-hidden="true" />
            </span>
            <p className="font-fwmono text-[11px] font-medium uppercase tracking-[0.16em] text-fw-violet">{text.doneEyebrow}</p>
            <h2 id="wa-suspension-popup-title" className="mt-4 text-[1.65rem] font-bold tracking-tight text-fw-ink sm:text-3xl">
              {text.doneTitle}
            </h2>
            <p className="mt-4 text-base leading-7 text-fw-graphite">{text.doneBody(name.trim().split(' ')[0])}</p>
            <button type="button" onClick={() => setOpen(false)} className={cn(fwSubmitClass, 'mt-8 max-w-xs')}>
              {text.doneButton}
            </button>
          </div>
        ) : (
          <div className="px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-6 sm:px-8 sm:py-8">
            <p className="flex items-center gap-2.5 pe-12 font-fwmono text-[11px] font-medium uppercase tracking-[0.16em] text-fw-violet">
              <span className="h-px w-[22px] bg-fw-violet" aria-hidden />
              {text.eyebrow}
            </p>
            <h2 id="wa-suspension-popup-title" className="mt-3 text-[1.4rem] font-bold leading-[1.15] tracking-tight text-fw-ink sm:text-[1.6rem]">
              {text.title}
            </h2>
            <p className="mt-2 text-base leading-6 text-fw-graphite">{text.body}</p>
            <ul className="mt-3 grid gap-1.5 text-base leading-snug text-fw-ink">
              {text.benefits.map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-fw-lavender text-fw-violet">
                    <Check size={13} strokeWidth={2.2} aria-hidden="true" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <form onSubmit={submit} className="mt-5 flex flex-col gap-2.5" noValidate>
              <label className="flex flex-col gap-1">
                <span className={cn('text-[13px] font-medium', nameInvalid ? 'text-fw-error' : 'text-fw-ink')}>
                  {text.name} <span className="text-fw-violet">*</span>
                </span>
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  onBlur={() => { setTouched((current) => ({ ...current, name: true })); validate('name', name) }}
                  autoComplete="name"
                  placeholder={text.namePlaceholder}
                  className={cn(fwInputClass, nameInvalid && fwInputErrorClass)}
                />
              </label>

              <div className="flex flex-col gap-1">
                <span className={cn('text-[13px] font-medium', phoneInvalid ? 'text-fw-error' : 'text-fw-ink')}>
                  {text.phone} <span className="text-fw-violet">*</span>
                </span>
                <div ref={countryRef} className="relative" dir="ltr">
                  <div
                    className={cn(
                      'flex h-[50px] items-center overflow-hidden rounded-xl border bg-fw-pure transition-[border-color,box-shadow] duration-150',
                      phoneInvalid ? 'border-fw-error' : phoneFocused ? 'border-fw-violet shadow-fw-focus' : 'border-fw-hairline',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setShowPicker((current) => !current)}
                      aria-label={text.country}
                      className="flex h-full shrink-0 items-center gap-1.5 bg-transparent pl-3.5 pr-2.5"
                    >
                      <FlagImg code={country.code} />
                      <ChevronDown size={11} className="text-fw-muted" strokeWidth={2.2} />
                    </button>
                    <span className="h-6 w-px shrink-0 bg-fw-hairline" aria-hidden="true" />
                    <span className="shrink-0 select-none px-2.5 text-[13px] text-fw-graphite">{country.dial}</span>
                    <input
                      value={phone}
                      onChange={(event) => {
                        setPhone(event.target.value)
                        if (event.target.value) validate('phone', event.target.value)
                      }}
                      onFocus={() => setPhoneFocused(true)}
                      onBlur={() => {
                        setPhoneFocused(false)
                        setTouched((current) => ({ ...current, phone: true }))
                        validate('phone', phone)
                      }}
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder={text.phonePlaceholder}
                      className="min-w-0 flex-1 bg-transparent pr-3.5 text-[15px] font-semibold tracking-[0.02em] text-fw-ink outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-fw-muted"
                    />
                  </div>

                  {showPicker ? (
                    <div className="absolute left-0 top-[calc(100%+4px)] z-30 max-h-[210px] w-full overflow-y-auto rounded-xl border border-fw-hairline bg-fw-white shadow-fw-panel">
                      {COUNTRIES.map((item) => (
                        <button
                          key={item.code}
                          type="button"
                          onClick={() => {
                            setCountry(item)
                            setShowPicker(false)
                            if (phone) validate('phone', phone, item.dial)
                          }}
                          className={cn(
                            'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] text-fw-ink',
                            country.code === item.code ? 'bg-fw-lavender font-semibold' : 'hover:bg-fw-mist-soft',
                          )}
                        >
                          <FlagImg code={item.code} />
                          <span className="flex-1">{item.name}</span>
                          <span className="text-[11px] text-fw-muted">{item.dial}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <label className="flex cursor-pointer select-none items-center gap-2">
                <input
                  type="checkbox"
                  checked={isWhatsApp}
                  onChange={(event) => setIsWhatsApp(event.target.checked)}
                  className="size-[15px] accent-emerald-500"
                />
                <span className="text-xs text-fw-muted">{text.isWhatsApp}</span>
              </label>

              {[nameInvalid && errors.name, phoneInvalid && errors.phone, formError].filter(Boolean).map((message) => (
                <p key={message as string} role="alert" className={fwErrorClass}>
                  <AlertCircle size={13} strokeWidth={2.2} />
                  {message}
                </p>
              ))}

              <p className={cn(fwConsentClass, 'flex items-start gap-2')}>
                <ShieldCheck size={15} strokeWidth={2} className="mt-0.5 shrink-0 text-fw-violet" />
                <span>
                  {text.privacy}{' '}
                  <Link to={href('/privacy', locale)} className="font-medium text-fw-ink">{text.privacyLink}</Link>
                </span>
              </p>

              <button type="submit" disabled={submitting} className={cn(fwSubmitClass, 'mt-1')}>
                {submitting ? (
                  <><Loader2 size={17} className="animate-spin" /> {text.sending}</>
                ) : (
                  <>{text.submit} <ArrowRight size={17} className="rtl:rotate-180" /></>
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
